import { ReactNode, useRef } from "react";
import Modal from "react-modal";
import styled, { createGlobalStyle } from "styled-components/macro";
import IconButton from "../../IconButton";
import useOnClickOutside from "../../../hooks/useOnClickOutside";

const modalOuterStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    zIndex: "30000",
    width: "80vw",
    height: "80vh",
    maxWidth: "600px",
    maxHeight: "660px",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.10), 0px 0px 2px 0px rgba(0, 0, 0, 0.25)",
    padding: "29px 27px",
    overflow: "hidden",
    backgroundColor: "var(--menu-color)",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
};

const ANIMATION_MS = 150;
const PORTAL_CLASS_NAME = "general-base-modal";

// react-modal sets the content's `transform` inline, so slide with the
// independent `translate` property instead.
const ModalAnimation = createGlobalStyle`
  .${PORTAL_CLASS_NAME} {
    .ReactModal__Overlay {
      opacity: 0;
      transition: opacity ${ANIMATION_MS}ms ease-out;
    }

    .ReactModal__Overlay--after-open {
      opacity: 1;
    }

    .ReactModal__Overlay--before-close {
      opacity: 0;
    }

    .ReactModal__Content {
      translate: 0 -30px;
      transition: translate ${ANIMATION_MS}ms ease-out;
    }

    .ReactModal__Content--after-open {
      translate: none;
    }

    .ReactModal__Content--before-close {
      translate: 0 -30px;
    }
  }
`;

const ModalInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  overflow: hidden;
  width: 100%;

  .header {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    padding-bottom: 8px;

    // .header__title,
    // .close {
    //   flex-basis: 50%;
    // }

    .header__title {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      text-align: left;
      gap: 5px;

      .main-title {
        font-size: 20px;
        font-style: normal;
        font-weight: 500;
        line-height: 1.3;
        color: var(--grey-0);
        display: flex;
        align-items: baseline;
        gap: 10px;

        .title-label {
          color: var(--grey-2);
          font-weight: 600;
          letter-spacing: 0.1em;
          font-size: 16px;
        }
      }

      .sub-title {
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 1.3;
        color: var(--grey-1);
      }
    }
    .close {
      text-align: right;
      cursor: pointer;
      /* Keep the close button right-aligned even when the .header__title
         is absent (parent flex's space-between has nothing to push
         against with a single child). */
      margin-left: auto;

      svg {
        g {
          opacity: 1;
        }
      }
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    justify-content: space-between;

    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: var(--grey-2);
      border-radius: 5px;
    }
  }

  .footer {
    margin-top: 10px;
    width: 100%;
    > div {
      gap: 12px;
    }

    .text-button-inner {
      padding: 9px 14px;
      line-height: normal;
    }

    a.button {
      display: inline-block;
      font-size: 16px;
      background: var(--primary-cta);
      color: white;
      border: 0;
      border-radius: 8px;
      font-family: Inter;
      font-weight: 500;
      padding: 15px 40px;
      cursor: pointer;
      margin-top: 15px;
      text-decoration: none;
      text-align: center;

      &.secondary {
        background: var(--grey-3);
        color: var(--grey-2);
      }
    }

    &.half {
      display: flex;
      gap: 10px;
      width: 100%;

      a {
        flex-basis: 50%;
      }
    }
  }
`;

interface ModalTitle {
  main?: string;
  sub?: string;
}

interface GeneralBaseModalProps {
  children: ReactNode;
  title?: ModalTitle;
  footer?: ReactNode;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isBeta?: boolean;
  onClose?: () => void;
}

export default function GeneralBaseModal({ children, title, footer, isOpen, setIsOpen, isBeta, onClose }: GeneralBaseModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  const onClickClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
    if (typeof setIsOpen === 'function') {
      setIsOpen(false);
    }
  };

  useOnClickOutside(modalRef, () => {
    if (isOpen) {
      onClickClose();
    }
  });


  return (
    <>
      <ModalAnimation />
      <Modal
        isOpen={isOpen}
        style={modalOuterStyles}
        onRequestClose={onClickClose}
        portalClassName={PORTAL_CLASS_NAME}
        closeTimeoutMS={ANIMATION_MS}
      >
        <ModalInnerContainer ref={modalRef}>
          <div className="header">
            {title && (
              <div className="header__title">
                <div className="main-title">
                  {title?.main}
                  {isBeta && <div className="title-label">BETA</div>}
                </div>
                <div className="sub-title">{title?.sub}</div>
              </div>
            )}
            <IconButton
              src="/assets/close.svg"
              onClick={onClickClose}
              triggerClassName="close"
              ariaLabel={`Close modal`}
              focusInset
            />
          </div>
          <div className="content">{children}</div>
          {footer && <div className="footer">{footer}</div>}
        </ModalInnerContainer>
      </Modal>
    </>
  );
}
